import { useCallback, useEffect, useRef, useState } from 'react';

// Composer dictation — the web counterpart of native's voice bar. Transcription
// runs entirely in the browser via the Web Speech API (SpeechRecognition), so
// there's no clip upload: the mic never leaves the device, and finals stream in
// live. While listening we still tap a parallel capture stream for a level meter
// + elapsed timer so the composer can show the native ✗ / waveform / ✓ bar.
// Confirm (✓) ends the session and hands the accumulated transcript to `onResult`
// (which sends it); cancel (✗) aborts and discards. `supported` resolves after
// mount (SSR renders false) so the mic fades in post-hydration.

type DictationStatus = 'idle' | 'recording' | 'transcribing';

// Rolling window of amplitude samples kept (newest last). Sized for the widest
// composer (desktop) — the waveform renders as many of the newest as fit its
// measured width, so a wide track never runs short of samples.
const LEVEL_WINDOW = 120;

// Minimal Web Speech API typings — not present in the TS DOM lib (and only ever
// exposed under the `webkit` prefix in the browsers that ship it).
interface SpeechRecognitionResultLike {
  isFinal: boolean;
  0: { transcript: string };
}
interface SpeechRecognitionEventLike {
  resultIndex: number;
  results: ArrayLike<SpeechRecognitionResultLike>;
}
interface SpeechRecognitionLike {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  start(): void;
  stop(): void;
  abort(): void;
  onresult: ((e: SpeechRecognitionEventLike) => void) | null;
  onerror: ((e: { error: string }) => void) | null;
  onend: (() => void) | null;
}
type SpeechRecognitionCtor = new () => SpeechRecognitionLike;

function getSpeechRecognitionCtor(): SpeechRecognitionCtor | null {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as {
    SpeechRecognition?: SpeechRecognitionCtor;
    webkitSpeechRecognition?: SpeechRecognitionCtor;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

export interface Dictation {
  supported: boolean;
  status: DictationStatus;
  // Elapsed recording time in whole seconds (for the "0:03" readout).
  seconds: number;
  // Recent mic amplitudes in [0,1], newest last — drives the waveform bars.
  levels: number[];
  start: () => void;
  // Stop + hand the transcript to onResult (the native ✓).
  confirm: () => void;
  // Abort + discard, nothing sent (the native ✗).
  cancel: () => void;
}

export function useDictation(onResult: (text: string) => void): Dictation {
  const [status, setStatus] = useState<DictationStatus>('idle');
  const [supported, setSupported] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [levels, setLevels] = useState<number[]>([]);
  const recognitionRef = useRef<SpeechRecognitionLike | null>(null);
  const transcriptRef = useRef('');
  const discardRef = useRef(false);
  // Set by confirm()/cancel() so onend finishes the session instead of the
  // auto-restart that keeps a continuous session alive across silence gaps.
  const stoppingRef = useRef(false);
  const streamRef = useRef<MediaStream | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const rafRef = useRef<number | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const onResultRef = useRef(onResult);
  onResultRef.current = onResult;

  useEffect(() => {
    setSupported(!!getSpeechRecognitionCtor());
  }, []);

  // Release the meter mic + tear down the meter/timer. Safe to call more than once.
  const teardownCapture = useCallback(() => {
    if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    if (timerRef.current != null) clearInterval(timerRef.current);
    timerRef.current = null;
    void audioCtxRef.current?.close().catch(() => {});
    audioCtxRef.current = null;
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  }, []);

  // Best-effort waveform: the Web Speech API owns its own mic and exposes no
  // amplitude data, so we tap a parallel capture stream purely to animate the
  // bars. Recognition works fine if this fails (permission race, no AudioContext).
  const startMeter = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!Ctor) return;
      const ctx = new Ctor();
      audioCtxRef.current = ctx;
      // A context created off a user gesture can still start SUSPENDED; while suspended the analyser
      // only ever reads silence (128 → rms 0), so the bars sit flat and never track the voice. Resume
      // it before the meter loop so the waveform actually reflects what's being said.
      if (ctx.state === 'suspended') await ctx.resume().catch(() => {});
      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 256;
      source.connect(analyser);
      const buf = new Uint8Array(analyser.fftSize);
      let lastSample = 0;
      const tick = () => {
        analyser.getByteTimeDomainData(buf);
        let sum = 0;
        for (let i = 0; i < buf.length; i++) {
          const x = (buf[i] - 128) / 128;
          sum += x * x;
        }
        const rms = Math.sqrt(sum / buf.length);
        const now = performance.now();
        if (now - lastSample > 70) {
          lastSample = now;
          // Gain up quiet speech, clamp to 1 so the bars always move.
          const level = Math.min(1, rms * 3.2);
          setLevels((prev) => [...prev.slice(-(LEVEL_WINDOW - 1)), level]);
        }
        rafRef.current = requestAnimationFrame(tick);
      };
      rafRef.current = requestAnimationFrame(tick);
    } catch {
      /* meter is decorative — recognition works without it */
    }
  }, []);

  const start = useCallback(async () => {
    if (recognitionRef.current) return;
    const Ctor = getSpeechRecognitionCtor();
    if (!Ctor) return;

    let recognition: SpeechRecognitionLike;
    try {
      recognition = new Ctor();
    } catch {
      return;
    }
    // The active app locale (stamped on <html lang> by _app), falling back to the
    // browser's UI language. An empty value lets the user agent pick its default.
    recognition.lang = (typeof document !== 'undefined' && document.documentElement.lang) || navigator.language || '';
    recognition.continuous = true;
    recognition.interimResults = false;
    transcriptRef.current = '';
    discardRef.current = false;
    stoppingRef.current = false;

    recognition.onresult = (e) => {
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const r = e.results[i];
        if (r.isFinal) transcriptRef.current += r[0].transcript;
      }
    };
    recognition.onerror = (e) => {
      // A denied mic (or no speech at all) ends the session with nothing to send.
      if (e.error === 'not-allowed' || e.error === 'service-not-allowed') {
        discardRef.current = true;
        stoppingRef.current = true;
      }
    };
    recognition.onend = () => {
      // Chrome auto-ends after a silence gap; while the user still has the
      // recorder open (hasn't hit ✓/✗) restart to keep one continuous session.
      if (!stoppingRef.current) {
        try {
          recognition.start();
          return;
        } catch {
          /* fall through and finish */
        }
      }
      teardownCapture();
      recognitionRef.current = null;
      const text = transcriptRef.current.trim();
      if (!discardRef.current && text) onResultRef.current(text);
      setStatus('idle');
    };

    try {
      recognition.start();
    } catch {
      return;
    }
    recognitionRef.current = recognition;
    setSeconds(0);
    setLevels([]);
    timerRef.current = setInterval(() => setSeconds((s) => s + 1), 1000);
    setStatus('recording');
    void startMeter();
  }, [startMeter, teardownCapture]);

  const confirm = useCallback(() => {
    discardRef.current = false;
    stoppingRef.current = true;
    const rec = recognitionRef.current;
    if (rec) {
      setStatus('transcribing');
      try {
        rec.stop();
      } catch {
        /* onend still fires and delivers the transcript */
      }
    }
  }, []);

  const cancel = useCallback(() => {
    discardRef.current = true;
    stoppingRef.current = true;
    const rec = recognitionRef.current;
    if (rec) {
      try {
        rec.abort();
      } catch {
        /* onend still fires and, with discard set, sends nothing */
      }
    } else {
      teardownCapture();
      setStatus('idle');
    }
  }, [teardownCapture]);

  useEffect(
    () => () => {
      stoppingRef.current = true;
      discardRef.current = true;
      try {
        recognitionRef.current?.abort();
      } catch {
        /* nothing to abort */
      }
      recognitionRef.current = null;
      teardownCapture();
    },
    [teardownCapture],
  );

  return { supported, status, seconds, levels, start, confirm, cancel };
}
